import { tryParseMoney } from '@sitzmark/core';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNotice } from '../../components/notice.js';
import { Button, Callout, Modal } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { ManagerApproval, approvalRefusal, isOverridable } from '../auth/manager-approval.js';
import type { ManagerOverride } from '../auth/manager-approval.js';

/**
 * Correct a size's cost once stock has come in at one (decision 126).
 *
 * The cost of stocked goods is the average of what that stock cost, and typing
 * over it would corrupt every average after — so the size's Edit dialog shows
 * it read-only and offers this instead. The stock on hand is revalued through
 * the ledger: nothing moves on the shelf, and the sentence above the button
 * says exactly what will change before anyone presses it (§2.2-6).
 *
 * Its own dialog rather than a section of the Edit dialog, because that one
 * already has its primary action (Save), and one screen gets one (DESIGN-DNA
 * Part 3). Cancel goes back to where it came from.
 */
export function CorrectCostDialog({
  variantId,
  label,
  costCents,
  onHand,
  onClose,
  onCorrected,
}: {
  variantId: string;
  label: string;
  costCents: number;
  onHand: number;
  onClose: () => void;
  onCorrected: () => void;
}) {
  const notify = useNotice();
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState('');
  const [needsApproval, setNeedsApproval] = useState(false);

  const next = typed.trim() === '' ? null : tryParseMoney(typed);
  const difference = next === null ? 0 : next - costCents;

  const correct = useMutation({
    mutationFn: (override?: ManagerOverride) =>
      api.products.correctCost.mutate({
        variantId,
        costCents: next!,
        note: note.trim(),
        ...(override === undefined ? {} : { override }),
      }),
    onSuccess: () => {
      recordBreadcrumb('stock', `corrected a cost: ${money(costCents)} → ${money(next!)}`);
      notify({ title: `Cost corrected — ${label}, ${money(costCents)} → ${money(next!)}` });
      onCorrected();
    },
    onError: (error) => {
      if (isOverridable(error)) setNeedsApproval(true);
    },
  });

  if (needsApproval) {
    return (
      <ManagerApproval
        title="Manager approval needed"
        because="Correcting a cost changes what the books say the stock on hand is worth."
        busy={correct.isPending}
        error={correct.isError ? approvalRefusal(correct.error) : null}
        onCancel={onClose}
        onApprove={(override) => correct.mutate(override)}
      />
    );
  }

  const blocked =
    typed.trim() === ''
      ? 'Type the correct cost'
      : next === null
        ? 'That is not an amount'
        : next === costCents
          ? 'That is already its cost'
          : note.trim() === ''
            ? 'Say what was wrong — the correction is the only record of itself'
            : undefined;

  return (
    <Modal
      open
      title={`Correct the cost of ${label}`}
      description={`${money(costCents)} each now · ${onHand} on hand`}
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={correct.isPending}
          disabledReason={blocked}
          onClick={() => correct.mutate(undefined)}
        >
          Correct the cost
        </Button>
      }
    >
      <div className="field">
        <label className="field__label" htmlFor="correct-cost">
          Correct cost each
        </label>
        <input
          id="correct-cost"
          className="input money"
          inputMode="decimal"
          autoFocus
          placeholder={(costCents / 100).toFixed(2)}
          value={typed}
          onChange={(event) => setTyped(event.target.value.replace(/[^\d.]/g, ''))}
        />
      </div>
      <div className="field">
        <label className="field__label" htmlFor="correct-cost-note">
          What was wrong
        </label>
        <input
          id="correct-cost-note"
          className="input"
          maxLength={200}
          placeholder="The invoice was $5 higher than the order"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>

      {next !== null && next !== costCents ? (
        <p className="text-secondary" role="status">
          {onHand > 0
            ? `Revalues the ${onHand} on hand from ${money(costCents)} to ${money(next)} each — ${money(Math.abs(difference * onHand))} ${difference > 0 ? 'more' : 'less'} in stock value. Nothing moves on the shelf. It is recorded in Reports → Adjustments as a cost correction.`
            : `Nothing is on hand, so only the cost changes. The next delivery averages from ${money(next)}.`}
        </p>
      ) : null}

      {correct.isError && !isOverridable(correct.error) ? (
        <Callout tone="danger">{errorMessage(correct.error)}</Callout>
      ) : null}
    </Modal>
  );
}
